import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added
import { User, MapPin, Calendar, Camera, Settings, Heart, LogOut, Edit3, Compass, Map, BookOpen, ChevronRight, CheckCircle2, Eye, ShieldAlert } from 'lucide-react';

const ProfilePage = () => {
  const { logout, user, token, isAuthChecking } = useAuth(); 
  const { isDark } = useTheme(); // 👈 Access Theme State
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('activities');
  const [toastMessage, setToastMessage] = useState('');
  const fileInputRef = useRef(null);

  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState({ name: "", role: "", phone: "" });
  
  const [myStories, setMyStories] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  const formatImageUrl = (backendPath) => {
    if (!backendPath) return null;
    if (backendPath.startsWith('http://localhost') || backendPath.startsWith('http://127.0.0.1')) {
      const url = new URL(backendPath);
      return url.pathname;
    }
    if (backendPath.startsWith('http')) return backendPath;
    const cleanPath = backendPath.replace('backend/', '');
    return cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  };

  useEffect(() => {
    if (isAuthChecking) return;
    let isMounted = true;

    const fetchUserDiaries = async (userEmail) => {
      try {
        const response = await fetch("/api/get_travel_stories");
        if (response.ok) {
          const data = await response.json();
          const filteredStories = data.travel_stories.filter(story => story.user_id === userEmail);
          
          const formattedStories = filteredStories.map(story => ({
            id: story.id,
            title: story.title,
            published: story.date,
            views: Math.floor(Math.random() * 500) + 50,
            likes: story.likes || 0,
            image: story.images?.length > 0 ? formatImageUrl(story.images[0]) : null
          }));
          
          if (isMounted) setMyStories(formattedStories);
          return formattedStories.length;
        }
      } catch (err) {
        console.error("Failed to fetch user stories", err);
      }
      return 0;
    };

    const fetchUserItineraries = async (activeToken) => {
      try {
        const response = await fetch("/api/get_itineraries", {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${activeToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.itineraries && data.itineraries.length > 0) {
            const sortedItineraries = data.itineraries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            
            const mappedActivities = sortedItineraries.map(itin => ({
              id: itin.id,
              type: itin.is_ai_generated ? "Route Planner" : "Custom Itinerary",
              title: itin.title || `Trip to ${itin.dest}`,
              date: new Date(itin.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
              status: "Planned",
              image: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=400&q=80",
              desc: itin.description || `A ${itin.days} planned journey.`,
              rawItinerary: itin
            }));
            
            if (isMounted) setRecentActivities(mappedActivities);
            return mappedActivities.length;
          }
        }
      } catch (err) {
        console.error("Failed to fetch user itineraries", err);
      }
      return 0;
    };

    const fetchProfile = async () => {
      let activeToken = token || localStorage.getItem('access_token');
      let activeUser = user;
      
      if (activeToken === 'undefined' || activeToken === 'null') activeToken = null;

      if (!activeUser) {
        try {
          const stored = localStorage.getItem('user_data');
          if (stored && stored !== 'undefined' && stored !== 'null' && stored !== '[object Object]') {
            activeUser = JSON.parse(stored);
          }
        } catch (e) {
          localStorage.removeItem('user_data');
        }
      }

      if (!activeToken && !activeUser) {
        if (isMounted) setLoading(false);
        return;
      }

      let emailForDiaries = activeUser?.email;
      let tripsCount = 0;

      if (activeToken) {
        try {
          tripsCount = await fetchUserItineraries(activeToken);

          const response = await fetch("/api/profile", {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${activeToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok && isMounted) {
            const data = await response.json();
            emailForDiaries = data.email;
            const diaryCount = await fetchUserDiaries(emailForDiaries);
            data.profile_picture = data.profile_picture
                                ? formatImageUrl(`http://127.0.0.1:8000${data.profile_picture}`)
                                : null;
            hydrateProfileData(data, diaryCount, tripsCount); 
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn("Background sync offline. Relying on local session.");
        }
      }

      if (emailForDiaries && isMounted) {
        const diaryCount = await fetchUserDiaries(emailForDiaries);
        hydrateProfileData(activeUser, diaryCount, tripsCount);
        setLoading(false);
      }
    };

    const hydrateProfileData = (sourceData, diaryCount, tripsCount) => {
      const userName = sourceData.name || sourceData.email.split('@')[0];
      setUserProfile({
        name: userName,
        role: sourceData.email === "amrit.business@odessey.in" ? "Business Analyst & Travel Enthusiast" : "Travel Enthusiast",
        joinDate: sourceData.email === "amrit.business@odessey.in" ? "Joined Jan 2024" : "Joined Recently",
        avatar: sourceData.profile_picture || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80",
        email: sourceData.email,
        phone: sourceData.mobile_number || sourceData.phone || "",
        stats: sourceData.email === "amrit.business@odessey.in" 
          ? { trips: tripsCount > 0 ? tripsCount : 14, diaries: diaryCount, saved: 28 } 
          : { trips: tripsCount || 0, diaries: diaryCount, saved: 0 }
      });

      setEditForm({
        name: userName,
        role: sourceData.email === "amrit.business@odessey.in" ? "Business Analyst & Travel Enthusiast" : "Travel Enthusiast",
        phone: sourceData.mobile_number || sourceData.phone || ""
      });
    };

    fetchProfile();
    return () => { isMounted = false; };
  }, [user, token, isAuthChecking]);

  const handleForceDemoBypass = () => {
    const demoPayload = {
      name: "Amrit",
      role: "Business Analyst & Travel Enthusiast",
      joinDate: "Joined Jan 2024",
      avatar: "https://images.wallpapersden.com/image/download/doraemon-minimal_a21ma2iUmZqaraWkpJRobWllrWdma2U.jpg",
      email: "amrit.business@odessey.in",
      phone: "",
      stats: { trips: 14, diaries: 3, saved: 28 }
    };
    
    setRecentActivities([
      { id: 1, type: "Mountain Expedition", title: "Backpacking Through Meghalaya", date: "Monsoon 2008", status: "Completed", image: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=400&q=80", desc: "Exploring misty hills, hidden waterfalls, living root bridges, and the untouched beauty of Northeast India." },
      { id: 2, type: "Urban Discovery", title: "Mumbai Through Local Eyes", date: "2024", status: "Completed", image: "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?auto=format&fit=crop&w=400&q=80", desc: "From heritage streets and marine sunsets to iconic cafés and street food trails, Mumbai felt alive at every corner." }
    ]);

    localStorage.setItem('access_token', 'dev_bypass_token');
    localStorage.setItem('user_data', JSON.stringify(demoPayload));
    setUserProfile(demoPayload);
    setEditForm({ name: "Amrit", role: "Business Analyst & Travel Enthusiast", phone: "" });
  };

  const handleSaveChanges = async () => {
    try {
      const activeToken = token || localStorage.getItem('access_token');
      const response = await fetch("/api/profile/update", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${activeToken}` },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        setUserProfile(prev => ({ ...prev, ...editForm }));
        showToast("Profile settings saved successfully!");
      } else {
        setUserProfile(prev => ({ ...prev, name: editForm.name, phone: editForm.phone }));
        showToast("Changes updated locally!");
      }
    } catch (err) {
      setUserProfile(prev => ({ ...prev, name: editForm.name, phone: editForm.phone }));
      showToast("Changes updated locally!");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setUserProfile(prev => ({ ...prev, avatar: reader.result }));
    };
    reader.readAsDataURL(file);

    try {
      const activeToken = token || localStorage.getItem('access_token');
      
      if (!activeToken || activeToken === 'dev_bypass_token') {
        showToast("Profile picture updated locally (Demo Mode)!");
        return;
      }

      const formData = new FormData();
      formData.append('profile_picture', file);

      const response = await fetch("/api/change_profile_picture", {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${activeToken}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(prev => ({ 
          ...prev, 
          avatar: formatImageUrl(`http://127.0.0.1:8000${data.profile_picture}`) 
        }));
        showToast("Profile picture uploaded successfully!");
      } else {
        showToast("Failed to upload profile picture to server.");
      }
    } catch (err) {
      console.error("Upload Error:", err);
      showToast("Error uploading profile picture.");
    }
  };

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleEditClick = () => {
    setActiveTab('settings');
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  if (loading || isAuthChecking) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? '#9ca3af' : '#4b5563', gap: '15px', transition: 'background-color 0.3s' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Synchronizing User Workspace...</span>
        <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: isDark ? '#111827' : '#f9fafb', padding: '40px 16px', textAlign: 'center', transition: 'background-color 0.3s', boxSizing: 'border-box' }}>
        <ShieldAlert size={48} color="#dc2626" style={{ marginBottom: '15px' }} />
        <h2 style={{ color: isDark ? '#f9fafb' : '#111827', margin: '0 0 10px 0', fontSize: '24px', fontWeight: 'bold' }}>Authentication Required</h2>
        <p style={{ color: isDark ? '#9ca3af' : '#6b7280', maxWidth: '400px', margin: '0 0 25px 0', fontSize: '15px', lineHeight: '1.5' }}>
          We couldn't find an active login session. Please return to the home page to log in, or use the developer bypass to view the dashboard UI layout instantly.
        </p>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={handleForceDemoBypass} style={{ padding: '14px 28px', backgroundColor: '#16a34a', color: 'white', borderRadius: '12px', border: 'none', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 12px rgba(22,163,74,0.2)' }}>
            Force Demo Bypass Mode
          </button>
          <button onClick={() => navigate('/')} style={{ padding: '14px 28px', backgroundColor: isDark ? '#374151' : '#f3f4f6', color: isDark ? 'white' : '#4b5563', borderRadius: '12px', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>
            Go to Home to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', paddingBottom: '80px', position: 'relative', transition: 'background-color 0.3s', boxSizing: 'border-box' }}>
      <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />

      <div style={{ height: '220px', backgroundColor: isDark ? '#030712' : '#111827', position: 'relative', overflow: 'hidden', transition: 'background-color 0.3s' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.3 }} />
      </div>

      <div style={{ maxWidth: '1000px', margin: '-90px auto 0', padding: '0 16px', position: 'relative', zIndex: 10, boxSizing: 'border-box' }}>
        
        <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', padding: '24px 20px', boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.5)' : '0 20px 40px rgba(0,0,0,0.08)', display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-end', marginBottom: '35px', border: isDark ? '1px solid #374151' : 'none', transition: 'background-color 0.3s, border-color 0.3s', boxSizing: 'border-box' }}>
          <div style={{ position: 'relative', marginTop: '-70px' }}>
            <img src={userProfile.avatar} alt="Profile" style={{ width: '130px', height: '130px', borderRadius: '50%', border: isDark ? '6px solid #1f2937' : '6px solid white', objectFit: 'cover', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }} />
            <button onClick={() => fileInputRef.current.click()} style={{ position: 'absolute', bottom: '8px', right: '8px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '50%', width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 10px rgba(22, 163, 74, 0.3)', transition: 'transform 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.transform='scale(1.1)'} onMouseLeave={(e)=>e.currentTarget.style.transform='scale(1)'}>
              <Camera size={15} />
            </button>
          </div>

          <div style={{ flex: '1 1 240px' }}>
            <h1 style={{ fontSize: 'clamp(26px, 4vw, 32px)', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', margin: '0 0 4px 0' }}>{userProfile.name}</h1>
            <p style={{ fontSize: '15px', color: isDark ? '#d1d5db' : '#4b5563', margin: '0 0 8px 0', fontWeight: '500' }}>{userProfile.role}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: isDark ? '#9ca3af' : '#9ca3af' }}>
              <Calendar size={13} /> {userProfile.joinDate}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: window.innerWidth < 640 ? '100%' : 'auto' }}>
            <button onClick={handleEditClick} style={{ flex: window.innerWidth < 640 ? 1 : 'initial', padding: '10px 20px', borderRadius: '10px', backgroundColor: isDark ? '#374151' : '#f3f4f6', color: isDark ? '#d1d5db' : '#4b5563', fontWeight: 'bold', border: isDark ? '1px solid #4b5563' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '14px', transition: 'background 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#4b5563' : '#e5e7eb'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#374151' : '#f3f4f6'}>
              <Edit3 size={16} /> Edit Profile
            </button>
            <button onClick={handleLogout} style={{ flex: window.innerWidth < 640 ? 1 : 'initial', padding: '10px 20px', borderRadius: '10px', backgroundColor: isDark ? '#450a0a' : '#fee2e2', color: '#dc2626', fontWeight: 'bold', border: isDark ? '1px solid #7c2d12' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '14px', transition: 'background 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#7f1d1d' : '#fca5a5'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#450a0a' : '#fee2e2'}>
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '35px' }}>
          {[
            { label: 'Trips Planned', value: userProfile.stats.trips, icon: Map, color: '#0284c7', bg: isDark ? '#082f49' : '#e0f2fe' },
            { label: 'Travel Diaries', value: userProfile.stats.diaries, icon: BookOpen, color: '#16a34a', bg: isDark ? '#064e3b' : '#dcfce7' },
            { label: 'Saved to Wishlist', value: userProfile.stats.saved, icon: Heart, color: '#ea580c', bg: isDark ? '#431407' : '#ffedd5' }
          ].map((stat, idx) => (
            <div key={idx} style={{ backgroundColor: isDark ? '#1f2937' : 'white', padding: '20px', borderRadius: '18px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: isDark ? '0 4px 15px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,0,0,0.02)', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxSizing: 'border-box', transition: 'background-color 0.3s' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', backgroundColor: stat.bg, color: stat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <stat.icon size={24} />
              </div>
              <div>
                <h3 style={{ fontSize: '24px', fontWeight: '900', color: isDark ? '#f9fafb' : '#111827', margin: 0 }}>{stat.value}</h3>
                <p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', margin: 0, fontWeight: '600' }}>{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '8px', borderBottom: isDark ? '2px solid #374151' : '2px solid #e5e7eb', marginBottom: '25px', overflowX: 'auto' }} className="hide-scrollbar">
          {[
            { id: 'activities', label: 'Recent Activities', icon: Compass },
            { id: 'diaries', label: 'My Diaries', icon: BookOpen },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '12px 20px', fontSize: '14px', fontWeight: 'bold', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: activeTab === tab.id ? '#16a34a' : (isDark ? '#9ca3af' : '#6b7280'), borderBottom: activeTab === tab.id ? '3px solid #16a34a' : '3px solid transparent', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>

        <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
          
          {/* --- RECENT ACTIVITIES TAB --- */}
          {activeTab === 'activities' && (
            <div style={{ display: 'grid', gap: '16px' }}>
              {recentActivities.length > 0 ? (
                recentActivities.map(activity => (
                  <div key={activity.id} style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '18px', padding: '16px', display: 'flex', gap: '16px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxShadow: isDark ? '0 4px 15px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,0,0,0.02)', alignItems: 'center', flexWrap: 'wrap', boxSizing: 'border-box', transition: 'background-color 0.3s' }}>
                    <img src={activity.image} alt={activity.title} style={{ width: '100px', height: '100px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }} />
                    <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDark ? '#a7f3d0' : '#16a34a', backgroundColor: isDark ? '#064e3b' : '#dcfce7', padding: '3px 8px', borderRadius: '50px', textTransform: 'uppercase' }}>{activity.type}</span>
                        <span style={{ fontSize: '12px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '3px' }}><Calendar size={13} /> {activity.date}</span>
                      </div>
                      <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: '0 0 6px 0', lineHeight: '1.3' }}>{activity.title}</h3>
                      <p style={{ fontSize: '13px', color: isDark ? '#d1d5db' : '#4b5563', margin: 0, lineHeight: '1.5' }}>{activity.desc}</p>
                    </div>
                    <button 
                      onClick={() => navigate('/plan', { state: { presetDestination: activity.dest, prefillPackage: activity.rawItinerary } })} 
                      style={{ padding: '10px 16px', borderRadius: '10px', backgroundColor: isDark ? '#064e3b' : '#f0fdf4', color: isDark ? '#a7f3d0' : '#16a34a', fontWeight: 'bold', border: isDark ? '1px solid #065f46' : '1px solid #bbf7d0', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px' }}
                    >
                      View Route <ChevronRight size={15} />
                    </button>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '35px 16px', color: isDark ? '#9ca3af' : '#6b7280', fontSize: '14px', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '18px', border: isDark ? '1px dashed #4b5563' : '1px dashed #cbd5e1', boxSizing: 'border-box' }}>
                  No recent activities found. Start planning your custom adventures!
                </div>
              )}
            </div>
          )}

          {activeTab === 'diaries' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
              {myStories.length > 0 ? (
                myStories.map(diary => (
                  <div key={diary.id} style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '18px', padding: '20px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxShadow: isDark ? '0 4px 15px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,0,0,0.02)', boxSizing: 'border-box', transition: 'background-color 0.3s' }}>
                    {diary.image && (
                      <img src={diary.image} alt="Story cover" style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '10px', marginBottom: '12px' }} />
                    )}
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '12px', lineHeight: '1.3' }}>{diary.title}</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#9ca3af', flexWrap: 'wrap', gap: '8px' }}>
                      <span>Published: {diary.published}</span>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Eye size={13} /> {diary.views}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Heart size={13} /> {diary.likes}</span>
                      </div>
                    </div>
                    <button onClick={() => navigate('/story')} style={{ width: '100%', marginTop: '16px', padding: '10px', borderRadius: '8px', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? '#d1d5db' : '#4b5563', fontWeight: 'bold', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', cursor: 'pointer', transition: 'background 0.2s', fontSize: '13px' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#374151' : '#f3f4f6'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#111827' : '#f9fafb'}>
                      View Diary
                    </button>
                  </div>
                ))
              ) : (
                <div style={{ padding: '20px', color: isDark ? '#9ca3af' : '#6b7280', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  You haven't published any stories yet.
                </div>
              )}
              
              <div onClick={() => navigate('/story', { state: { openUploadModal: true } })} style={{ backgroundColor: isDark ? '#111827' : '#f8fafc', borderRadius: '18px', padding: '20px', border: isDark ? '2px dashed #4b5563' : '2px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', minHeight: '140px', boxSizing: 'border-box' }} onMouseEnter={(e)=> {e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.backgroundColor = isDark ? '#064e3b' : '#f0fdf4'}} onMouseLeave={(e)=> {e.currentTarget.style.borderColor = isDark ? '#4b5563' : '#cbd5e1'; e.currentTarget.style.backgroundColor = isDark ? '#111827' : '#f8fafc'}}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: isDark ? '#1f2937' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                  <Camera size={20} color="#16a34a" />
                </div>
                <h4 style={{ fontSize: '15px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: 0 }}>Write a new Diary</h4>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', padding: '25px 20px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxShadow: isDark ? '0 4px 15px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,0,0,0.02)', boxSizing: 'border-box', transition: 'background-color 0.3s' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '18px' }}>Account Preferences</h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '500px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Display Name</label>
                  <input type="text" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '14px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Profile Bio / Tagline</label>
                  <input type="text" value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})} placeholder="e.g. Adventure Seeker & Foodie" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '14px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Phone Number</label>
                  <input type="tel" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} placeholder="+91 98765 43210" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '14px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Email Address</label>
                  <input type="email" value={userProfile.email} disabled style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '10px', border: isDark ? '1px solid #374151' : '1px solid #d1d5db', backgroundColor: isDark ? '#111827' : '#f3f4f6', color: isDark ? '#9ca3af' : '#6b7280', cursor: 'not-allowed', fontSize: '14px' }} />
                  <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Email address cannot be changed directly.</p>
                </div>

                <button onClick={handleSaveChanges} style={{ alignSelf: 'flex-start', marginTop: '8px', padding: '12px 24px', borderRadius: '10px', backgroundColor: '#16a34a', color: 'white', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#15803d'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#16a34a'}>
                  <CheckCircle2 size={16} /> Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {toastMessage && (
        <div style={{ position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#111827', color: 'white', padding: '12px 24px', borderRadius: '50px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', zIndex: 9999, animation: 'slideUp 0.3s ease-out forwards', boxSizing: 'border-box' }}>
          <CheckCircle2 size={18} color="#4ade80" /> {toastMessage}
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default ProfilePage;